import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    ListItem,
    ListItemText,
    Typography,
} from '@material-ui/core';

import { showToast } from '@klpkonsern/ui';
import { useCurrentUser } from '@klp/auth';
import { Block } from '@klp/design';
import { ActionButton } from '@klp/design/ActionButton';
import { IconName, Illustration } from '@klp/design/Illustrations';

import { useRemoveUserMutation } from '../rtkApi';

interface IRemoveUserDialogProps {
    email: string;
    onSuccess: () => void;
}

export const RemoveUserDialog: React.FC<IRemoveUserDialogProps> = ({
    email,
    onSuccess,
}) => {
    const { t } = useTranslation();
    const [displayDialog, setDisplayDialog] = useState(false);
    const [removeUser, { isLoading: removeUserProgress }] =
        useRemoveUserMutation();
    const { currentUser: user } = useCurrentUser();

    const handleRemoveUser = () => {
        setDisplayDialog(true);
    };

    const handleRemoveUserClose = () => {
        if (!removeUserProgress) {
            setDisplayDialog(false);
        }
    };

    const handleRemoveUserConfirmed = async () => {
        try {
            await removeUser(email).unwrap();
            showToast({
                body: t('userSearch.removeUserSuccess'),
                type: 'success',
            });
            onSuccess();
            setDisplayDialog(false);
        } catch (error) {
            showToast({
                body: t('userSearch.removeUserError'),
                type: 'error',
            });
        }
    };

    return (
        <>
            <Block>
                {email && email === user?.email ? null : (
                    <ListItem
                        dense={true}
                        button={true}
                        onClick={() => handleRemoveUser()}
                    >
                        <Block paddingRight="compact">
                            <Illustration icon={IconName.CLOSE} size="medium" />
                        </Block>
                        <ListItemText>
                            <Typography
                                style={{
                                    color: 'red',
                                }}
                            >
                                {t('userSearch.removeUserTitle')}
                            </Typography>
                        </ListItemText>
                    </ListItem>
                )}
            </Block>
            <Dialog open={displayDialog} onClose={handleRemoveUserClose}>
                <DialogTitle>{t('userSearch.removeUserTitle')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('userSearch.removeUserQuestion', { email })}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <ActionButton
                        actionType="cancel"
                        translation="general.decline"
                        onClick={handleRemoveUserClose}
                        disabled={removeUserProgress}
                    />
                    <ActionButton
                        actionType="forward"
                        translation="general.confirm"
                        onClick={handleRemoveUserConfirmed}
                        isLoading={removeUserProgress}
                    />
                </DialogActions>
            </Dialog>
        </>
    );
};
